import {getTestProps} from '@benchling/js-test-utils';
import {Box, Button, createStyles, Divider, Group, Loader, Stack, Text, Title} from '@mantine/core';
import React, {useRef, useState} from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {downloadSpreadsheetTemplate} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {
  NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_PLURAL,
  NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_SINGULAR,
  NdcSpreadsheetComponentFormatType,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import Antibody from 'antibodies/visualization/Antibody';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {SPREADSHEET_MIME_TYPE, TaskState} from 'bulk-import/SpreadsheetBulkImportConstants';
import notifications from 'notifications';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import * as ImportUtil from 'util/ImportUtil';
import * as net from 'util/Net';

const useStyles = createStyles((theme) => ({
  spreadsheetBannerContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.sm,
  },
  spreadsheetChooser: {
    '&.drop-help': {
      margin: 0,
    },
  },
  templateDownloadSection: {
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray[0],
    borderRadius: theme.radius.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
  },
  templateDownloadText: {
    flex: 1,
  },
  errorList: {
    paddingInlineStart: theme.spacing.lg,
  },
}));

export interface UploadSpreadsheetPaneProps {
  startingType: NdcSpreadsheetComponentFormatType;
  spreadsheetFile: File | null;
  setSpreadsheetFile: (spreadsheetFile: File) => void;
  setSpreadsheetURL: (spreadsheetURL: string) => void;
  clearSpreadsheet: () => void;
  columnInferenceErrorMessages: Array<string>;
  isInferringColumns: boolean;
  selectedAntibodyFormat: AntibodyFormat;
}

export default function UploadSpreadsheetPane({
  startingType,
  spreadsheetFile,
  setSpreadsheetFile,
  setSpreadsheetURL,
  clearSpreadsheet,
  columnInferenceErrorMessages,
  isInferringColumns,
  selectedAntibodyFormat,
}: UploadSpreadsheetPaneProps): JSX.Element {
  const {classes} = useStyles();

  const [uploadState, setUploadState] = useState<TaskState | null>(null);
  /** This ref helps us avoid race conditions where a user uploads a file and then immediately clears and
   * uploads another file. We increment it with each upload, and with each clear. This ensures that when an a
   * S3 upload completes, we only update state and the machine if it's the most recent upload, and the
   * spreadsheet was not cleared in the meantime. */
  const uploadIdRef = useRef(0);

  const handleFileSelect = async (file: File): Promise<void> => {
    const currentUploadId = ++uploadIdRef.current;
    setUploadState(TaskState.STARTED);
    try {
      setSpreadsheetFile(file);
      const url = await ImportUtil.upload(file);
      if (uploadIdRef.current !== currentUploadId) {
        return;
      }
      assert(url != null, 'Failed to get a valid S3 URL');
      setSpreadsheetURL(url);
      setUploadState(TaskState.COMPLETED);
    } catch (err) {
      if (uploadIdRef.current !== currentUploadId) {
        return;
      }
      setUploadState(TaskState.FAILED);
      if (net.isServerRequestError(err)) {
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
  };

  const handleClearSpreadsheet = (): void => {
    uploadIdRef.current++;
    clearSpreadsheet();
    setUploadState(null);
  };

  return (
    <div>
      <Group spacing="md" align="center" noWrap={true} mb="lg">
        <Box
          w="50px"
          h="50px"
          px="5px"
          py="7px"
          bg="gray.0"
          sx={(theme) => ({
            borderRadius: theme.radius.md,
            border: `1px solid ${theme.colors.gray[3]}`,
          })}
        >
          <Antibody format={selectedAntibodyFormat} />
        </Box>
        {/* We need to re-enable wrapping since Group noWrap gets inherited */}
        <Stack spacing={0} style={{flex: 1, minWidth: 0, whiteSpace: 'normal'}}>
          <Title order={5} fw={600}>
            Protein {NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_PLURAL[startingType]}
          </Title>
          <Text size="sm">
            Upload a spreadsheet containing your protein{' '}
            {NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_PLURAL[startingType]}
          </Text>
        </Stack>
      </Group>

      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={`Ensure the spreadsheet is formatted with a column for each protein ${NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_SINGULAR[startingType]}`}
        supportingInfo={
          <div className={classes.spreadsheetBannerContent}>
            <p>
              For each {NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_SINGULAR[startingType]} column, enter either raw
              sequence bases/amino acids or an existing Benchling entity name or registry ID. Each{' '}
              {NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_SINGULAR[startingType]} column should use one type
              consistently.
            </p>
            <img
              src={`/static/img/antibody-spreadsheet-import-${NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_PLURAL[
                startingType
              ].replace(/ /g, '-')}-example.svg`}
              alt={`Example spreadsheet format for protein ${
                NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_PLURAL[startingType]
              }`}
            />
          </div>
        }
        action={{
          actionType: InlineBannerActionType.ACCORDION,
          accordionLabels: {moreLabel: 'Show details', lessLabel: 'Hide details'},
          rightAlign: true,
          initialShowMore: true,
        }}
      />

      <Divider my="lg" />

      <div className={classes.templateDownloadSection}>
        <div className={classes.templateDownloadText}>
          <Text weight={600} size="sm">
            Don't have a spreadsheet ready?
          </Text>
          <Text size="sm" color="dimmed">
            Download a template with columns that match your selected format
          </Text>
        </div>
        <Button
          // eslint-disable-next-line no-restricted-syntax
          variant="light"
          size="md"
          onClick={() => {
            downloadSpreadsheetTemplate(selectedAntibodyFormat, startingType);
          }}
        >
          Download template
        </Button>
      </div>

      <Text size="sm" mb="lg">
        The maximum number of rows you can import is{' '}
        {BenchlingConfig.ANTIBODY_NESTED_DATA_CREATION_MAX_ANTIBODIES_PER_RUN}.
      </Text>

      {spreadsheetFile ? (
        <FileList>
          <FileRow
            file={spreadsheetFile}
            onRequestRemove={handleClearSpreadsheet}
            additionalContent={
              uploadState === TaskState.STARTED || isInferringColumns ? (
                <Loader
                  size="sm"
                  {...getTestProps({component: 'UploadSpreadsheetPane', element: 'fileRowLoader'})}
                />
              ) : undefined
            }
          />
        </FileList>
      ) : (
        <FileChooser
          accept={SPREADSHEET_MIME_TYPE}
          multiple={false}
          onSelectFiles={handleFileSelect}
          className={classes.spreadsheetChooser}
        />
      )}

      {uploadState === TaskState.FAILED && (
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Error uploading spreadsheet" />
      )}

      {columnInferenceErrorMessages.length === 1 && (
        <InlineBanner bannerType={InlineBannerType.ERROR} text={columnInferenceErrorMessages[0]} />
      )}
      {columnInferenceErrorMessages.length > 1 && (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Your file contains multiple errors. Please resolve the following errors and re-upload your spreadsheet"
          supportingInfo={
            <ul className={classes.errorList}>
              {columnInferenceErrorMessages.map((message, index) => (
                <li key={index}>{message}</li>
              ))}
            </ul>
          }
        />
      )}
    </div>
  );
}
